import { getTranslations } from "@/lib/translations";
import { z } from "zod";

import { requireStaff } from "@/core/auth/admin-guard";
import { createArticleService } from "@/core/services/factory";
import { getArticleSharePath } from "@/core/services/types/article.types";
import type {
  ArticleSuggestionMutationResult,
  ArticleSuggestionSearchEventsResult,
  ArticleSuggestionSearchPlacesResult,
} from "@/core/services/types/article.types";

function mapMutationError(
  error: string,
  t: ReturnType<typeof getTranslations>,
): string {
  if (error === "not_found") return t("articleNotFound");
  return t("mutationFailed");
}

const articleIdSchema = z.uuid();
const relatedIdSchema = z.uuid();
const searchQuerySchema = z.string().trim().max(100);

export async function addArticleSuggestedPlace(
  articleId: string,
  placeId: string,
): Promise<ArticleSuggestionMutationResult> {
  await requireStaff();

  const t = getTranslations("articles.edit.suggestions");
  const parsedArticleId = articleIdSchema.safeParse(articleId);
  const parsedPlaceId = relatedIdSchema.safeParse(placeId);

  if (!parsedArticleId.success || !parsedPlaceId.success) {
    return { success: false, error: t("mutationFailed") };
  }

  const result = await createArticleService().addSuggestedPlace(
    parsedArticleId.data,
    parsedPlaceId.data,
  );

  if (!result.success) {
    return { success: false, error: mapMutationError(result.error, t) };
  }
  return result;
}

export async function removeArticleSuggestedPlace(
  articleId: string,
  placeId: string,
): Promise<ArticleSuggestionMutationResult> {
  await requireStaff();

  const t = getTranslations("articles.edit.suggestions");
  const parsedArticleId = articleIdSchema.safeParse(articleId);
  const parsedPlaceId = relatedIdSchema.safeParse(placeId);

  if (!parsedArticleId.success || !parsedPlaceId.success) {
    return { success: false, error: t("mutationFailed") };
  }

  const result = await createArticleService().removeSuggestedPlace(
    parsedArticleId.data,
    parsedPlaceId.data,
  );

  if (!result.success) {
    return { success: false, error: mapMutationError(result.error, t) };
  }
  return result;
}

export async function addArticleSuggestedEvent(
  articleId: string,
  eventId: string,
): Promise<ArticleSuggestionMutationResult> {
  await requireStaff();

  const t = getTranslations("articles.edit.suggestions");
  const parsedArticleId = articleIdSchema.safeParse(articleId);
  const parsedEventId = relatedIdSchema.safeParse(eventId);

  if (!parsedArticleId.success || !parsedEventId.success) {
    return { success: false, error: t("mutationFailed") };
  }

  const result = await createArticleService().addSuggestedEvent(
    parsedArticleId.data,
    parsedEventId.data,
  );

  if (!result.success) {
    return { success: false, error: mapMutationError(result.error, t) };
  }
  return result;
}

export async function removeArticleSuggestedEvent(
  articleId: string,
  eventId: string,
): Promise<ArticleSuggestionMutationResult> {
  await requireStaff();

  const t = getTranslations("articles.edit.suggestions");
  const parsedArticleId = articleIdSchema.safeParse(articleId);
  const parsedEventId = relatedIdSchema.safeParse(eventId);

  if (!parsedArticleId.success || !parsedEventId.success) {
    return { success: false, error: t("mutationFailed") };
  }

  const result = await createArticleService().removeSuggestedEvent(
    parsedArticleId.data,
    parsedEventId.data,
  );

  if (!result.success) {
    return { success: false, error: mapMutationError(result.error, t) };
  }
  return result;
}

export async function searchArticleSuggestionPlaces(
  query: string,
  excludePlaceIds: string[] = [],
): Promise<ArticleSuggestionSearchPlacesResult> {
  await requireStaff();

  const t = getTranslations("articles.edit.suggestions");
  const parsedQuery = searchQuerySchema.safeParse(query);
  const parsedExclude = z.array(relatedIdSchema).safeParse(excludePlaceIds);

  if (!parsedQuery.success || !parsedExclude.success) {
    return { success: false, error: t("searchFailed") };
  }

  return createArticleService().searchPlacesForSuggestion(
    parsedQuery.data,
    parsedExclude.data,
  );
}

export async function searchArticleSuggestionEvents(
  query: string,
  excludeEventIds: string[] = [],
): Promise<ArticleSuggestionSearchEventsResult> {
  await requireStaff();

  const t = getTranslations("articles.edit.suggestions");
  const parsedQuery = searchQuerySchema.safeParse(query);
  const parsedExclude = z.array(relatedIdSchema).safeParse(excludeEventIds);

  if (!parsedQuery.success || !parsedExclude.success) {
    return { success: false, error: t("searchFailed") };
  }

  return createArticleService().searchEventsForSuggestion(
    parsedQuery.data,
    parsedExclude.data,
  );
}
